import React from 'react';
import { useTranslation } from 'react-i18next';
import { HoursMode, ListingStatus, OpeningHoursResult } from '../../types';
import { BadgeTone, statusBadge } from '../../utils/listingDisplay';

const TONE_CLASSES: Record<BadgeTone, string> = {
  open: 'bg-emerald-50 text-emerald-800',
  soon: 'bg-gold-50 text-gold-700',
  closed: 'bg-danger-50 text-danger-700',
  neutral: 'bg-fog text-ash',
};

/** Open / closed badge. Always text plus color, never color alone. */
export function OpenStatusBadge({
  listing,
  result,
  className = '',
}: {
  listing: { hoursMode: HoursMode; hours: unknown[] };
  result: OpeningHoursResult | undefined;
  className?: string;
}) {
  const { t } = useTranslation();
  const badge = statusBadge(listing, result);
  if (!badge) return null;

  const values: Record<string, string> = { ...badge.values };
  if (badge.dayOfWeek !== undefined) values.day = t(`directory.dayOfWeekShort.${badge.dayOfWeek}`);
  let text = t(`directory.${badge.key}`, values);
  if (badge.nextDay) text += ` ${t('directory.openStatus.nextDay')}`;
  if (badge.closedPrefix) text = `${t('directory.openStatus.closed')} · ${text}`;

  return (
    <span
      className={`inline-flex items-center text-xs px-2 py-0.5 rounded-[10px] font-medium ${TONE_CLASSES[badge.tone]} ${className}`}
    >
      {text}
    </span>
  );
}

/** Pending / draft / hidden tag, shown to the super admin and owners. Nothing for published. */
export function ListingStatusTag({ status }: { status: ListingStatus }) {
  const { t } = useTranslation();
  if (status === 'published') return null;
  return (
    <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-[10px] font-medium border border-dashed border-ink/30 text-ink/70">
      {t(`directory.listingStatus.${status}`)}
    </span>
  );
}
